import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryConfig';
import { userVerificationApi } from '../../services/userVerificationApi';
import {
  ALLOWED_FILE_TYPES,
  getMaxFileSize,
  isValidFileType,
} from '../../../features/user-verifications/types';

interface UploadVerificationParams {
  verificationType: 'company';
  verificationId: string;
  companyId?: string;
  file: File;
  targetUserId?: string; // For admin uploads on behalf of users
}

interface ReviewVerificationParams {
  verificationType: 'company';
  userVerificationId: string;
  action: 'approve' | 'reject';
  rejectionReason?: string;
  expiryDate?: string;
}

/**
 * Hook to upload and submit a verification document
 * Implements the 3-step S3 presigned URL upload process
 */
export const useUploadVerification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      verificationType,
      verificationId,
      companyId,
      file,
      targetUserId,
    }: UploadVerificationParams) => {
      // Validate file type
      if (!isValidFileType(file.type)) {
        throw new Error(
          `Tipo de archivo no permitido. Tipos permitidos: ${ALLOWED_FILE_TYPES.join(', ')}`
        );
      }

      // Validate file size
      const maxSize = getMaxFileSize(file.type);
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        throw new Error(`El archivo excede el tamaño máximo de ${maxSizeMB}MB`);
      }

      // Step 1: Get presigned URL
      const uploadUrlResponse = await userVerificationApi.getUploadUrl(
        verificationType,
        verificationId,
        file.type,
        file.name
      );

      if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
        throw new Error(uploadUrlResponse.error || 'Error al obtener URL de subida');
      }

      const { presignedPost, key } = uploadUrlResponse.data;

      // Step 2: Upload directly to S3
      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([fieldKey, value]) => {
        formData.append(fieldKey, value as string);
      });
      // File must be last!
      formData.append('file', file);

      const s3Response = await fetch(presignedPost.url, {
        method: 'POST',
        body: formData,
      });

      if (!s3Response.ok) {
        throw new Error('Error al subir el archivo a S3');
      }

      // Step 3: Submit verification
      const submitData = {
        fileKey: key,
        fileName: file.name,
        fileMimeType: file.type,
        fileSize: file.size,
        targetUserId, // Include target user ID for admin uploads
      };

      if (!companyId) {
        throw new Error('Se requiere ID de compañía para verificaciones de empresa');
      }
      const submitResponse = await userVerificationApi.submitCompanyVerification(
        companyId,
        verificationId,
        submitData
      );

      if (!submitResponse.success) {
        throw new Error(submitResponse.error || 'Error al enviar verificación');
      }

      return { success: true, fileKey: key, targetUserId };
    },
    onSuccess: (data) => {
      // Invalidate verification queries to refresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.userVerifications.me() });
      // Also invalidate the target user's verifications if uploading on their behalf
      if (data.targetUserId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.userVerifications.user(data.targetUserId),
        });
      }
    },
  });
};

/**
 * Hook to review (approve/reject) a verification (admin)
 */
export const useReviewVerification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      verificationType,
      userVerificationId,
      action,
      rejectionReason,
      expiryDate,
    }: ReviewVerificationParams) => {
      const data = {
        action,
        rejectionReason,
        expiryDate,
      };

      const response = await userVerificationApi.reviewCompanyVerification(
        userVerificationId,
        data
      );

      if (!response.success) {
        throw new Error(response.error || 'Error al revisar verificación');
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate all verification-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.userVerifications.all });
    },
  });
};

/**
 * Hook to initialize verifications for a user (admin)
 */
export const useInitializeUserVerifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await userVerificationApi.initializeUserVerifications(userId);
      if (!response.success) {
        throw new Error(response.error || 'Error al inicializar verificaciones');
      }
      return response;
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.userVerifications.user(userId),
      });
    },
  });
};

/**
 * Hook to view a verification document
 */
export const useViewDocument = () => {
  return useMutation({
    mutationFn: async ({
      verificationType,
      userVerificationId,
    }: {
      verificationType: 'company';
      userVerificationId: string;
    }) => {
      const response = await userVerificationApi.getDocumentUrl(
        verificationType,
        userVerificationId
      );

      if (!response.success || !response.data) {
        throw new Error('Error al obtener documento');
      }

      return response.data;
    },
  });
};
